/* eslint-disable array-element-newline */
import {action, computed, makeObservable, observable, reaction} from 'mobx';
import {RingBuilderApi} from '../services/api';
import {SETTING_STEP, DIAMOND_STEP, PRODUCT_SELECTION_STEP, REVIEW_STEP, PRODUCT_ATTRIBUTES_PARAM_KEY, START_WITH_SETTING_URI, START_WITH_LAB_DIAMOND_URI, START_WITH_DIAMOND_URI, DEFAULT_TITLE} from './constants';
import debounce from '@mvps-genesis/debounce';
import {DiamondStore} from './diamond-store';
import {SettingStore} from './setting-store';
import {MessagesStore} from './messages-store';

export class RingBuilderStore {
	#storageState;

	step;

	stepOrder;

	startingStep;

	/** @type {import('./diamond-store').DiamondStore} */
	diamond;

	apiEndpoint;

	debug;

	/** @type {import('./setting-store').SettingStore} */
	setting;

	screenSize;

	screen;

	isTouch;

	stopStepReaction;

	resizeHandler;

	loaded = false;

	review;

	ringBuilderUrl;

	uri;

	mergeState (storageState) {
		this.diamond = typeof this.diamond === 'object'
			? this.diamond
			: new DiamondStore({
				labGrown: this.labGrown,
				parentStore: this
			});
		this.diamond.mergeState({product: storageState.diamond});

		this.setting = typeof this.setting === 'object'
			? this.setting
			: new SettingStore({
				parentStore: this
			});
		this.setting.mergeState({product: storageState.setting});

		return this;
	}

	constructor ({
		apiEndpoint = '',
		debounceDelay = 1000,
		debug = false,
		diamond = '',
		labGrown = '',
		screen = '',
		scrollDiamondIntoView = '',
		setting = '',
		startingStep = '',
		stepOrder = '',
		review = '',
		ringBuilderUrl = '',
		uri = ''
	} = {}) {
		// Set default device state
		// eslint-disable-next-line no-undef
		this.isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;
		this.calculateScreenSize();

		this.debounceDelay = debounceDelay;
		this.client = new RingBuilderApi(apiEndpoint);
		this.apiEndpoint = apiEndpoint;
		this.debug = debug;
		this.screen = screen;
		this.scrollDiamondIntoView = scrollDiamondIntoView;
		this.review = review;
		this.startingStep = startingStep;
		this.stepOrder = stepOrder
			? stepOrder?.split(',')?.map(v => parseInt(v, 10))
			: null;
		this.ringBuilderUrl = ringBuilderUrl;
		this.uri = uri;

		// Create messages store
		this.messagesStore = new MessagesStore();

		// Determine initial labgrown state
		this.labGrown = this.determineInitialLabGrown(labGrown);

		this.#storageState = {
			diamond: diamond,
			setting: setting
		};
	}

	// eslint-disable-next-line max-lines-per-function
	async init () {
		// Merge in state from the remote storage object (mivaJS) / set defaults
		this.mergeState(this.#storageState);

		// Run init functions for diamond and setting stores
		await Promise.all([this.diamond.init(), this.setting.init()]);

		// Apply compatibility background facets
		this.applyProductCompatabilityParams();

		// Apply lab grown facet logic
		this.diamond.applyBackgroundFacets(this.diamond.labGrownFacet);

		makeObservable(this, {
			calculateScreenSize: action,
			diamondStepHref: computed,
			editDiamondHref: computed,
			editSettingHref: computed,
			loaded: observable,
			mergeState: action,
			removeDiamondStepHref: computed,
			removeSettingStepHref: computed,
			reviewStepDisabled: computed,
			reviewStepHref: computed,
			screenSize: observable,
			setLoaded: action,
			setScrollDiamondIntoView: action,
			setStep: action,
			settingStepHref: computed,
			step: observable,
			urlSearchParams: computed
		});

		this.bindEvents();

		// Determine the correct step we are on
		this.stepOrder ||= this.determineStepOrder();
		this.setStep(this.determineStartingStep());
		this.startingStepTracking();

		// Determine if the diamond preview should be enabled
		if (this.step === PRODUCT_SELECTION_STEP && !this.diamond?.product?.id) {
			this.diamond.setPreview({
				enabled: true
			});
		}
		else {
			this.diamond.setPreview({
				enabled: false
			});
		}

		return this;
	}

	bindEvents () {
		// Create reaction so we can push changes to our remote storage endpoint
		this.stopStepReaction = reaction(
			() => this.step,
			this.executeProductStoreSearch.bind(this)
		);

		this.resizeHandler = debounce(500, () => this.calculateScreenSize());
		window.addEventListener('resize', this.resizeHandler);
	}

	unbindEvents () {
		this.stopStepReaction();

		window.removeEventListener('resize', this.resizeHandler);
	}

	applyProductCompatabilityParams () {
		this.diamond.applyCompatabilityParams(this.setting.product);
		this.setting.applyCompatabilityParams(this.diamond.product);
	}

	get urlSearchParams () {
		const attributes = Array
			.from(new URLSearchParams(window.location.search).entries())
			.filter(([key]) => key.startsWith(PRODUCT_ATTRIBUTES_PARAM_KEY) === true);

		return new URLSearchParams([
			...this.stepOrder
				? [
					[
						'so',
						this.stepOrder
					]
				]
				: [],
			...this.labGrown
				? [
					[
						'lg',
						this.labGrown
					]
				]
				: [],
			...this.debug
				? [
					[
						'debug',
						this.debug
					]
				]
				: [],
			...this.diamond?.product?.id
				? [
					[
						'diamond',
						this.diamond.product.id
					]
				]
				: [],
			...this.setting?.product?.id
				? [
					[
						'setting',
						this.setting.product.id
					]
				]
				: [],
			...attributes
		]);
	}

	get settingStepHref () {
		const url = new URL(this.ringBuilderUrl, window.location.origin);
		for (let [key, value] of this.urlSearchParams) {
			if (key.startsWith(PRODUCT_ATTRIBUTES_PARAM_KEY)) {
				continue;
			}
			url.searchParams.set(key, value);
		}
		url.searchParams.set('s', SETTING_STEP);
		return url.toString();
	}

	get diamondStepHref () {
		const url = new URL(this.ringBuilderUrl, window.location.origin);
		for (let [key, value] of this.urlSearchParams) {
			url.searchParams.set(key, value);
		}
		url.searchParams.set('s', DIAMOND_STEP);
		return url.toString();
	}

	get removeSettingStepHref () {
		const url = new URL(this.settingStepHref);
		url.searchParams.set('setting', '');
		return url.toString();
	}

	get removeDiamondStepHref () {
		const url = new URL(this.diamondStepHref);
		url.searchParams.set('diamond', '');
		return url.toString();
	}

	get reviewStepSearchParams () {
		return new URLSearchParams([
			[
				'review',
				'1'
			]
		]);
	}

	get reviewStepHref () {
		const url = new URL(this.setting?.product?.url || '', window.location.origin);
		for (let [key, value] of [...this.urlSearchParams, ...this.reviewStepSearchParams]) {
			url.searchParams.set(key, value);
		}
		return url.toString();
	}

	get editDiamondHref () {
		const url = new URL(this.diamond?.previousUrl || '', window.location.origin);
		for (let [key, value] of this.urlSearchParams) {
			url.searchParams.set(key, value);
		}
		// Scroll Diamond into View
		url.searchParams.set('sdtv', '1');
		return url.toString();
	}

	get editSettingHref () {
		const url = new URL(this.setting?.product?.url || '', window.location.origin);
		for (let [key, value] of this.urlSearchParams) {
			url.searchParams.set(key, value);
		}
		return url.toString();
	}

	get reviewStepDisabled () {
		return !this.diamond?.product?.id || !this.setting?.product?.id;
	}

	determineInitialLabGrown (labGrown) {
		if (this.uri === START_WITH_LAB_DIAMOND_URI) {
			return 1;
		}

		return parseInt(labGrown, 10) || 0;
	}

	determineStepOrder () {
		switch (this.uri) {
			default:
			case START_WITH_SETTING_URI: {
				return [
					SETTING_STEP,
					DIAMOND_STEP
				];
			}

			case START_WITH_LAB_DIAMOND_URI:
			case START_WITH_DIAMOND_URI: {
				return [
					DIAMOND_STEP,
					SETTING_STEP
				];
			}
		}
	}

	determineStartingStep () {
		if (this.startingStep) {
			return this.startingStep;
		}
		if (this.review) {
			return REVIEW_STEP;
		}
		if (this.screen === 'PROD') {
			return PRODUCT_SELECTION_STEP;
		}
		switch (this.uri) {
			default:
			case START_WITH_SETTING_URI: {
				if (this.setting?.product?.id) {
					return DIAMOND_STEP;
				}
				return SETTING_STEP;
			}

			case START_WITH_LAB_DIAMOND_URI:
			case START_WITH_DIAMOND_URI: {
				if (this.diamond?.product?.id) {
					return SETTING_STEP;
				}
				return DIAMOND_STEP;
			}
		}
	}

	setLoaded (bool) {
		this.loaded = Boolean(bool);
		return this;
	}

	setStep (step) {
		this.step = parseInt(step, 10);

		return this;
	}

	startingStepTracking () {
		if (this.diamond?.product?.id || this.setting?.product?.id) {
			return;
		}

		if (this.step === SETTING_STEP) {
			this.trackStartingStep('Setting');
		} else if (this.step === DIAMOND_STEP) {
			this.trackStartingStep(this.labGrown ? 'Lab Diamond' : 'Diamond');
		} else if (this.step === PRODUCT_SELECTION_STEP) {
			this.trackStartingStep('Setting Product Page');
		}
	}

	trackStartingStep (label) {
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({
			event: 'ga_event',
			/* eslint-disable camelcase */
			ga_event_action: 'Start',
			ga_event_category: 'Ring Builder',
			ga_event_label: label,
			ga_event_non_interaction: true
			/* eslint-enable camelcase */
		});
	}

	setScrollDiamondIntoView (scrollDiamondIntoView) {
		this.setScrollDiamondIntoView = scrollDiamondIntoView;
	}

	calculateScreenSize () {
		if (window.matchMedia('(min-width: 1170px)').matches) {
			this.screenSize = 'desktop';
		}
		else if (window.matchMedia('(min-width: 76px)').matches) {
			this.screenSize = 'tablet';
		}
		else {
			this.screenSize = 'mobile';
		}
	}

	clear () {
		this.unbindEvents();
		this.mergeState({});
		this.bindEvents();
	}

	executeProductStoreSearch () {
		switch (this.step) {
			case DIAMOND_STEP:
				this.diamond.searchService.search();
				break;
			default:
			case SETTING_STEP:
				this.setting.searchService.search();
				break;
		}
	}
};

export const ringBuilderStore = new RingBuilderStore(window.mivaJS.ringBuilder).init();
